import type { InvitationPreview } from "@cafegestor/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/app/auth";
import { Alert, Button, Card, Spinner } from "@/components/ui";
import { apiFetch, errorMessage } from "@/lib/api";
import { setActiveCompanyId } from "@/lib/company-store";

export function AcceptInvitePage({ token }: { token: string }) {
  const { status, session, needsPassword, signOut } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const preview = useQuery({
    queryKey: ["convite", token],
    queryFn: () =>
      apiFetch<InvitationPreview>(`/convites/previa?token=${encodeURIComponent(token)}`, {
        withCompany: false,
      }),
    enabled: status === "signed_in" && token.length > 0,
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () =>
      apiFetch<{ companyId: string }>("/convites/aceitar", {
        method: "POST",
        body: { token },
        withCompany: false,
      }),
    onSuccess: async ({ companyId }) => {
      setActiveCompanyId(companyId);
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      await navigate({ to: needsPassword ? "/definir-senha" : "/" });
    },
  });

  let content;
  if (!token) content = <Alert tone="error">Link de convite incompleto.</Alert>;
  else if (status === "loading") content = <Spinner />;
  else if (status === "signed_out")
    content = (
      <>
        <p className="text-sm">Entre com o e-mail que recebeu o convite para aceitá-lo.</p>
        <Link
          to="/login"
          className="mt-4 inline-flex h-10 items-center rounded-md bg-primary px-4 text-sm text-primary-foreground"
        >
          Entrar
        </Link>
        <p className="mt-3 text-xs text-muted-foreground">
          Depois de entrar, abra de novo o link do convite.
        </p>
      </>
    );
  else if (preview.isPending) content = <Spinner />;
  else if (preview.isError) content = <Alert tone="error">{errorMessage(preview.error)}</Alert>;
  else if (!preview.data.emailMatches)
    content = (
      <>
        <Alert tone="error">
          Este convite foi enviado para outro e-mail. Você está conectado como {session?.user.email}
          .
        </Alert>
        <Button className="mt-4" variant="secondary" onClick={() => void signOut()}>
          Sair e entrar com outro e-mail
        </Button>
      </>
    );
  else if (preview.data.status !== "pending")
    content = (
      <Alert tone="warn">
        {preview.data.status === "expired"
          ? "Este convite expirou. Peça um novo convite."
          : "Este convite já foi usado ou foi cancelado."}
      </Alert>
    );
  else
    content = (
      <>
        <p>
          Você foi convidado(a) para <strong>{preview.data.companyName}</strong> com o papel{" "}
          <strong>{preview.data.roleName}</strong>.
        </p>
        {accept.isError && (
          <div className="mt-3">
            <Alert tone="error">{errorMessage(accept.error)}</Alert>
          </div>
        )}
        <Button className="mt-4" onClick={() => accept.mutate()} disabled={accept.isPending}>
          Aceitar convite
        </Button>
      </>
    );

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/50 px-4">
      <Card title="Convite" className="w-full max-w-md">
        {content}
      </Card>
    </div>
  );
}
